import {
  ESCALATION_KIND_LABELS,
  ESCALATION_STATUS_LABELS,
  type EscalationKind,
  type EscalationLevel,
  type EscalationRow,
  type EscalationStatus,
} from "@estelle/domain";
import {
  CircleHelp,
  PackageX,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * The small things Escalations draws on every surface: the kind (with its
 * level), the status, and the "sent before" note (#702). Each says its word in
 * text and carries an icon besides, so colour is never the only signal.
 */

const KIND_STYLE: Record<
  EscalationKind,
  { icon: LucideIcon; className: string }
> = {
  failed_delivery: {
    icon: TriangleAlert,
    className: "border-transparent bg-tint-yellow text-tint-yellow-foreground",
  },
  no_update: {
    icon: CircleHelp,
    className: "border-transparent bg-tint-orange text-tint-orange-foreground",
  },
  lost: {
    icon: PackageX,
    className: "border-destructive/40 bg-destructive/10 text-destructive",
  },
};

/** "Failed delivery · Level 2": what went wrong and how many times. */
export function KindBadge({
  kind,
  level,
  className,
}: {
  kind: EscalationKind;
  level: EscalationLevel | null;
  className?: string;
}) {
  const { icon: Icon, className: tone } = KIND_STYLE[kind];
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 font-medium", tone, className)}
    >
      <Icon aria-hidden className="size-3.5 shrink-0" />
      {ESCALATION_KIND_LABELS[kind]}
      {level === null ? null : (
        <span className="tabular-nums">· Level {level}</span>
      )}
    </Badge>
  );
}

const STATUS_CLASS: Record<EscalationStatus, string> = {
  new: "border-transparent bg-tint-blue text-tint-blue-foreground",
  sent: "border-border bg-muted text-foreground",
  resolved: "border-transparent bg-tint-green text-tint-green-foreground",
  dismissed: "border-border bg-muted text-muted-foreground",
};

export function StatusBadge({ status }: { status: EscalationStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 font-medium", STATUS_CLASS[status])}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      {ESCALATION_STATUS_LABELS[status]}
    </Badge>
  );
}

/** A `new` row that was sent at an earlier level says so, so the courier is chased again, not asked afresh. */
export function SentBefore({ row }: { row: EscalationRow }) {
  if (row.status !== "new" || row.levelAtLastSend === null) return null;
  if (row.level !== null && row.level === row.levelAtLastSend) return null;
  return (
    <span className="text-muted-foreground block text-xs">
      Sent before at level {row.levelAtLastSend}
    </span>
  );
}
